import { useEffect, useState } from 'react';
import { brand } from '../shared/brand';
import { chaptersFor } from './chapters';
import {
  CHAPTER_SETTLE_MS,
  HOST_WAIT_MS,
  POLL_MS,
  SCROLL_SETTLE_MS,
  SETUP_GAP_MS,
  TARGET_WAIT_MS,
} from './constants';
import { hideTip } from './tipBus';
import {
  ALL,
  ensureVisible,
  flattenSteps,
  resolveStepTargets,
  skipIndex,
} from './tourModel';

// The product tour (Implementation List §4), kept outside React so it
// survives route changes: the Forms list, the builder and Submissions are
// separate pages, and the tour walks across all three. Each page mounts
// FormsHelp, which registers itself here as a host with its `page` and a
// `navigate(where)` callback; the tour asks the newest host to set the
// screen up and waits for the right page to mount. It only runs when the
// user starts it from Help & tours; nothing starts it on its own.
//
// State: { running, chapterId, steps, index, ready, targets, hostsRev }.
// `ready` is false while a step sets up its screen and looks for its
// target; `targets` are the elements the spotlight frames.

// Screen requests that change page, and the page each one lands on. The
// others ('entry', 'export', 'close-overlays') act on the current page.
export const PAGE_FOR = {
  list: 'list',
  builder: 'builder',
  submissions: 'submissions',
};

const IDLE = {
  running: false,
  chapterId: null,
  steps: [],
  index: 0,
  ready: false,
  targets: [],
  hostsRev: 0,
};

let state = IDLE;
let seq = 0;
let dir = 1;
let lastChapterId = null;
const listeners = new Set();
const hosts = [];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn(state));
};

export const getTourState = () => state;

export const subscribeTour = (fn) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const useTourState = () => {
  const [current, setCurrent] = useState(getTourState);
  useEffect(() => {
    setCurrent(getTourState());
    return subscribeTour(setCurrent);
  }, []);
  return current;
};

// ---- hosts ----

export const activeHost = () => hosts[hosts.length - 1] || null;

export const isActiveHost = (id) => {
  const host = activeHost();
  return !!host && host.id === id;
};

// host: { id, page: 'list' | 'builder' | 'submissions', navigate(where) }.
export const registerHost = (host) => {
  hosts.push(host);
  setState({ hostsRev: state.hostsRev + 1 });
  return () => {
    const i = hosts.indexOf(host);
    if (i !== -1) hosts.splice(i, 1);
    setState({ hostsRev: state.hostsRev + 1 });
  };
};

const waitForHost = async (page, token) => {
  const end = Date.now() + HOST_WAIT_MS;
  for (;;) {
    if (token !== seq) return null;
    const host = activeHost();
    if (host && (!page || host.page === page)) return host;
    if (Date.now() >= end) return page ? null : host;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
};

// A host's navigate may throw or return a promise that rejects (no form to
// open, say): the chapter's targets then never show and its steps are
// skipped.
const callHost = (host, where) => {
  try {
    return Promise.resolve(host.navigate(where)).catch(() => undefined);
  } catch (e) {
    return Promise.resolve();
  }
};

// Run a chapter's screen requests in order. After a page change the next
// request goes to the page that mounted. With no host at all there is
// nothing to set up; the step looks for its target as the page stands.
const runSetup = async (commands, token) => {
  let host = await waitForHost(null, token);
  if (token !== seq) return false;
  if (!host) return true;
  for (let i = 0; i < commands.length; i += 1) {
    const where = commands[i];
    // eslint-disable-next-line no-await-in-loop
    await callHost(host, where);
    if (token !== seq) return false;
    if (PAGE_FOR[where]) {
      // eslint-disable-next-line no-await-in-loop
      const landed = await waitForHost(PAGE_FOR[where], token);
      if (token !== seq) return false;
      host = landed || activeHost();
      if (!host) return true;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(SETUP_GAP_MS);
    if (token !== seq) return false;
  }
  return true;
};

const waitForTargets = async (step, token) => {
  const end = Date.now() + (step.wait || TARGET_WAIT_MS);
  for (;;) {
    if (token !== seq) return [];
    const found = resolveStepTargets(document, step);
    if (found.length || Date.now() >= end) return found;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
};

// ---- running the tour ----

export const endTour = () => {
  if (!state.running) return;
  seq += 1;
  lastChapterId = null;
  setState({ ...IDLE, hostsRev: state.hostsRev });
  document.body.removeAttribute('data-touring');
  const host = activeHost();
  if (host) callHost(host, 'close-overlays');
};

// Set the screen up for the current step and find its target. A step whose
// target never appears is skipped in the direction the user was going.
const resolveStep = async () => {
  seq += 1;
  const token = seq;
  const step = state.steps[state.index];
  if (!step) return;
  setState({ ready: false, targets: [] });
  if (lastChapterId !== step.chapter.id) {
    lastChapterId = step.chapter.id;
    const ok = await runSetup(step.chapter.setup, token);
    if (!ok) return;
    await sleep(CHAPTER_SETTLE_MS);
    if (token !== seq) return;
  }
  if (step.before) {
    const ok = await runSetup([step.before], token);
    if (!ok) return;
  }
  const targets = await waitForTargets(step, token);
  if (token !== seq) return;
  if (!targets.length) {
    const next = skipIndex(state.index, dir, state.steps.length);
    if (next === -1) {
      endTour();
      return;
    }
    setState({ index: next });
    resolveStep();
    return;
  }
  ensureVisible(targets[0]);
  await sleep(SCROLL_SETTLE_MS);
  if (token !== seq) return;
  setState({ ready: true, targets });
};

// chapterId: 'all' for the full tour, or one chapter's id.
export const startTour = (chapterId = ALL) => {
  hideTip();
  dir = 1;
  lastChapterId = null;
  const steps = flattenSteps(chaptersFor(brand().name), chapterId);
  if (!steps.length) return;
  setState({
    running: true,
    chapterId,
    steps,
    index: 0,
    ready: false,
    targets: [],
  });
  document.body.setAttribute('data-touring', '');
  resolveStep();
};

export const nextStep = () => {
  if (!state.running || !state.ready) return;
  if (state.index >= state.steps.length - 1) {
    endTour();
    return;
  }
  dir = 1;
  setState({ index: state.index + 1 });
  resolveStep();
};

export const prevStep = () => {
  if (!state.running || state.index <= 0) return;
  dir = -1;
  setState({ index: state.index - 1 });
  resolveStep();
};

// Tests only.
export const resetTourForTests = () => {
  seq += 1;
  dir = 1;
  lastChapterId = null;
  hosts.splice(0, hosts.length);
  listeners.clear();
  state = IDLE;
  document.body.removeAttribute('data-touring');
};
